import { Button, Input } from "@/components/ui";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { aiChatSearchOptions } from "@/lib/data/react-query/ai-chat";
import type { AiChatSearchHit, ChatApi } from "@/lib/types/ai-chat";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, LoaderCircle, Search, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";

const SEARCH_DEBOUNCE_MS = 180;
const SEARCH_MINIMUM_CHARACTERS = 3;
const MAX_AUTOMATIC_RECONCILIATIONS = 20;

type SearchSelection = {
  itemId: string;
  occurrenceIndex: number;
};

export type ChatSearchPanelProps = {
  api: ChatApi;
  isNavigating?: boolean;
  onClose: () => void;
  onQueryChange?: (query: string) => void;
  onSelectMatch?: (
    hit: AiChatSearchHit,
    query: string,
    occurrenceIndex: number,
  ) => Promise<void> | void;
  threadId: string;
  visibleMatchCounts?: Readonly<Record<string, number | undefined>>;
};

function matchCount(
  hit: AiChatSearchHit,
  visibleMatchCounts: Readonly<Record<string, number | undefined>>,
): number {
  const visibleCount = visibleMatchCounts[hit.itemId];
  if (visibleCount === 2_000 && hit.matchCount > visibleCount) {
    return hit.matchCount;
  }
  return visibleCount === undefined ? hit.matchCount : visibleCount;
}

function selectableHitBefore(
  hits: AiChatSearchHit[],
  endIndex: number,
  visibleMatchCounts: Readonly<Record<string, number | undefined>>,
) {
  for (let index = endIndex - 1; index >= 0; index -= 1) {
    const hit = hits[index];
    if (matchCount(hit, visibleMatchCounts) > 0) return hit;
  }
  return undefined;
}

function keepInputFocus(event: MouseEvent<HTMLButtonElement>) {
  event.preventDefault();
}

export function ChatSearchPanel({
  api,
  isNavigating = false,
  onClose,
  onQueryChange,
  onSelectMatch,
  threadId,
  visibleMatchCounts = {},
}: ChatSearchPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const navigationRequestRef = useRef(0);
  const autoSelectedQueryRef = useRef<string>();
  const normalizedQueryRef = useRef("");
  const reconciledHitIdsRef = useRef(new Set<string>());
  const [query, setQuery] = useState("");
  const [selection, setSelection] = useState<SearchSelection>();
  const [isSelecting, setIsSelecting] = useState(false);
  const [navigationError, setNavigationError] = useState(false);
  const [reconciliationLimitReached, setReconciliationLimitReached] =
    useState(false);
  const normalizedQuery = query.trim();
  normalizedQueryRef.current = normalizedQuery;
  const debouncedQuery = useDebouncedValue(normalizedQuery, SEARCH_DEBOUNCE_MS);
  const queryIsSettling = normalizedQuery !== debouncedQuery;
  const search = useInfiniteQuery(
    aiChatSearchOptions(threadId, debouncedQuery, api),
  );
  const hits = useMemo(
    () => search.data?.pages.flatMap((page) => page.items) ?? [],
    [search.data?.pages],
  );
  const selectedHitIndex = selection
    ? hits.findIndex((hit) => hit.itemId === selection.itemId)
    : -1;
  const selectedHit =
    selectedHitIndex >= 0 ? hits[selectedHitIndex] : undefined;
  const totalLoadedOccurrences = hits.reduce(
    (total, hit) => total + matchCount(hit, visibleMatchCounts),
    0,
  );
  const selectedMatchCount = selectedHit
    ? matchCount(selectedHit, visibleMatchCounts)
    : 0;
  const currentOccurrence =
    selectedHit && selectedMatchCount > 0
      ? hits
          .slice(0, selectedHitIndex)
          .reduce(
            (count, hit) => count + matchCount(hit, visibleMatchCounts),
            0,
          ) +
        Math.min(selection?.occurrenceIndex ?? 0, selectedMatchCount - 1) +
        1
      : 0;
  const canSearch = normalizedQuery.length >= SEARCH_MINIMUM_CHARACTERS;
  const isSearching =
    canSearch && (queryIsSettling || search.isLoading || search.isFetching);
  const navigationIsBusy = isNavigating || isSelecting;
  const previousSelectableHit = selectableHitBefore(
    hits,
    selectedHit ? selectedHitIndex : hits.length,
    visibleMatchCounts,
  );
  const canSelectPrevious = Boolean(
    !selectedHit || !selection
      ? previousSelectableHit
      : selection.occurrenceIndex > 0 || previousSelectableHit,
  );
  const canSelectNext = Boolean(
    hits.length &&
      (!selectedHit ||
        !selection ||
        selection.occurrenceIndex + 1 < selectedMatchCount ||
        selectedHitIndex < hits.length - 1 ||
        search.hasNextPage),
  );

  const selectMatch = useCallback(
    async (
      hit: AiChatSearchHit,
      occurrenceIndex: number,
      querySnapshot: string,
    ) => {
      const request = ++navigationRequestRef.current;
      setSelection({ itemId: hit.itemId, occurrenceIndex });
      setNavigationError(false);
      setReconciliationLimitReached(false);
      if (!onSelectMatch) return;
      setIsSelecting(true);
      try {
        await onSelectMatch(hit, querySnapshot, occurrenceIndex);
      } catch {
        if (request === navigationRequestRef.current) {
          setNavigationError(true);
        }
      } finally {
        if (request === navigationRequestRef.current) setIsSelecting(false);
      }
    },
    [onSelectMatch],
  );

  const selectPrevious = useCallback(() => {
    if (!hits.length || navigationIsBusy || queryIsSettling) return;
    if (!selectedHit || !selection) {
      const lastHit = selectableHitBefore(
        hits,
        hits.length,
        visibleMatchCounts,
      );
      if (lastHit) {
        void selectMatch(
          lastHit,
          matchCount(lastHit, visibleMatchCounts) - 1,
          debouncedQuery,
        );
      }
      return;
    }
    if (selection.occurrenceIndex > 0) {
      void selectMatch(
        selectedHit,
        selection.occurrenceIndex - 1,
        debouncedQuery,
      );
      return;
    }
    if (previousSelectableHit) {
      void selectMatch(
        previousSelectableHit,
        matchCount(previousSelectableHit, visibleMatchCounts) - 1,
        debouncedQuery,
      );
    }
  }, [
    debouncedQuery,
    hits,
    navigationIsBusy,
    queryIsSettling,
    previousSelectableHit,
    selectMatch,
    selectedHit,
    selection,
    visibleMatchCounts,
  ]);

  const selectNext = useCallback(async () => {
    if (!hits.length || navigationIsBusy || queryIsSettling) return;
    if (!selectedHit || !selection) {
      const firstHit = hits[0];
      await selectMatch(firstHit, 0, debouncedQuery);
      return;
    }
    if (selection.occurrenceIndex + 1 < selectedMatchCount) {
      await selectMatch(
        selectedHit,
        selection.occurrenceIndex + 1,
        debouncedQuery,
      );
      return;
    }
    if (selectedHitIndex < hits.length - 1) {
      const nextHit = hits[selectedHitIndex + 1];
      await selectMatch(nextHit, 0, debouncedQuery);
      return;
    }
    if (!search.hasNextPage || search.isFetchingNextPage) return;
    const querySnapshot = debouncedQuery;
    const pagingRequest = ++navigationRequestRef.current;
    setIsSelecting(true);
    try {
      const result = await search.fetchNextPage();
      if (
        pagingRequest !== navigationRequestRef.current ||
        querySnapshot !== normalizedQueryRef.current
      ) {
        return;
      }
      const nextPageFirstHit = result.data?.pages.at(-1)?.items[0];
      setIsSelecting(false);
      if (nextPageFirstHit) {
        await selectMatch(nextPageFirstHit, 0, querySnapshot);
      }
    } finally {
      if (pagingRequest === navigationRequestRef.current) {
        setIsSelecting(false);
      }
    }
  }, [
    debouncedQuery,
    hits,
    navigationIsBusy,
    queryIsSettling,
    search,
    selectMatch,
    selectedHit,
    selectedHitIndex,
    selection,
    selectedMatchCount,
  ]);

  useEffect(() => {
    const shortcut = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "f") {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      } else if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", shortcut);
    return () => {
      window.removeEventListener("keydown", shortcut);
    };
  }, [onClose]);

  useEffect(() => {
    navigationRequestRef.current += 1;
    autoSelectedQueryRef.current = undefined;
    reconciledHitIdsRef.current.clear();
    setSelection(undefined);
    setIsSelecting(false);
    setNavigationError(false);
    setReconciliationLimitReached(false);
  }, [debouncedQuery, threadId]);

  useEffect(() => {
    if (
      !selectedHit ||
      !selection ||
      visibleMatchCounts[selectedHit.itemId] === undefined ||
      selection.occurrenceIndex < selectedMatchCount ||
      navigationIsBusy ||
      queryIsSettling
    ) {
      return;
    }
    if (selectedMatchCount > 0) {
      void selectMatch(selectedHit, selectedMatchCount - 1, debouncedQuery);
      return;
    }
    if (reconciledHitIdsRef.current.has(selectedHit.itemId)) return;
    reconciledHitIdsRef.current.add(selectedHit.itemId);
    if (
      !canSelectNext ||
      reconciledHitIdsRef.current.size >= MAX_AUTOMATIC_RECONCILIATIONS
    ) {
      setReconciliationLimitReached(true);
      return;
    }
    void selectNext();
  }, [
    debouncedQuery,
    canSelectNext,
    navigationIsBusy,
    queryIsSettling,
    selectMatch,
    selectNext,
    selectedHit,
    selectedMatchCount,
    selection,
    visibleMatchCounts,
  ]);

  useEffect(() => {
    if (
      queryIsSettling ||
      debouncedQuery.length < SEARCH_MINIMUM_CHARACTERS ||
      !hits[0] ||
      autoSelectedQueryRef.current === debouncedQuery
    ) {
      return;
    }
    autoSelectedQueryRef.current = debouncedQuery;
    void selectMatch(hits[0], 0, debouncedQuery);
  }, [debouncedQuery, hits, queryIsSettling, selectMatch]);

  const handleQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextQuery = event.target.value;
    navigationRequestRef.current += 1;
    setSelection(undefined);
    setNavigationError(false);
    setReconciliationLimitReached(false);
    setQuery(nextQuery);
    onQueryChange?.(nextQuery);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (event.shiftKey) selectPrevious();
      else void selectNext();
    }
  };

  const retryNavigation = () => {
    if (selectedHit && selection) {
      void selectMatch(selectedHit, selection.occurrenceIndex, debouncedQuery);
    }
  };

  return (
    <div
      role="search"
      aria-label="Find in conversation"
      className="border-border/70 bg-background/95 w-[min(28rem,calc(100vw-1.5rem))] rounded-xl border p-2 shadow-lg backdrop-blur"
    >
      <div className="flex items-center gap-1.5">
        <div className="relative min-w-0 flex-1">
          <Search
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2"
          />
          <Input
            ref={inputRef}
            autoFocus
            aria-label="Find in conversation"
            className="h-8 pr-2 pl-8 text-sm"
            placeholder="Find in conversation"
            value={query}
            onChange={handleQueryChange}
            onKeyDown={handleInputKeyDown}
          />
        </div>
        <span
          aria-live="polite"
          className="text-muted-foreground min-w-14 text-center text-xs tabular-nums"
        >
          {isSearching ? (
            <LoaderCircle className="mx-auto size-3.5 animate-spin" />
          ) : canSearch ? (
            `${currentOccurrence}/${totalLoadedOccurrences}${search.hasNextPage ? "+" : ""}`
          ) : (
            "—"
          )}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          disabled={!canSelectPrevious || navigationIsBusy || queryIsSettling}
          aria-label="Previous match"
          onMouseDown={keepInputFocus}
          onClick={selectPrevious}
        >
          <ChevronUp />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          disabled={
            !canSelectNext ||
            navigationIsBusy ||
            queryIsSettling ||
            search.isFetchingNextPage
          }
          aria-label="Next match"
          onMouseDown={keepInputFocus}
          onClick={() => {
            void selectNext();
          }}
        >
          <ChevronDown />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label="Close find"
          onClick={onClose}
        >
          <X />
        </Button>
      </div>
      {normalizedQuery.length > 0 && !canSearch ? (
        <p className="text-muted-foreground px-2 pt-1.5 text-xs">
          Enter at least three characters.
        </p>
      ) : search.error ? (
        <div
          role="alert"
          className="text-destructive flex items-center justify-between gap-3 px-2 pt-1.5 text-xs"
        >
          <span>Search failed.</span>
          <button
            type="button"
            className="underline underline-offset-2"
            onMouseDown={keepInputFocus}
            onClick={() => {
              void search.refetch();
            }}
          >
            Retry
          </button>
        </div>
      ) : reconciliationLimitReached ? (
        <p role="alert" className="text-muted-foreground px-2 pt-1.5 text-xs">
          Some source matches are not visible. Refine your search.
        </p>
      ) : navigationError ? (
        <div
          role="alert"
          className="text-destructive flex items-center justify-between gap-3 px-2 pt-1.5 text-xs"
        >
          <span>Could not open this match.</span>
          <button
            type="button"
            className="underline underline-offset-2"
            onMouseDown={keepInputFocus}
            onClick={retryNavigation}
          >
            Retry
          </button>
        </div>
      ) : canSearch && !isSearching && hits.length === 0 ? (
        <p
          aria-live="polite"
          className="text-muted-foreground px-2 pt-1.5 text-xs"
        >
          No matches.
        </p>
      ) : null}
    </div>
  );
}
